import React, { FC, useEffect, useState } from 'react';
import { keyCodeESC } from 'utility/constants';
import SVGIcon from 'views/components/primitive/SVGIcon';
import CloseIcon from 'assets/images/ic_close.svg';
import { Button } from 'views/components/primitive';
import NextIcon from 'assets/images/ic_right_arrow_24x24.svg';
import BackIcon from 'assets/images/ic_left_arrow_24x24.svg';
import { useKeydown, usePreventScroll } from 'hooks';
import { keyframes } from '@emotion/core';
import styled from '@emotion/styled';
import { Document, Page } from 'react-pdf';
import { bp, layer, palette } from 'entity/createTheme';
import LoadingIndicator from '../LoadingIndicator';
import pdfCMapOptions from 'utility/pdfCMapOptions';

type Props = {
  url: string;
  isOpen: boolean;
  toggleOpen(): void;
  pageNumber: number;
  syncPageNumber: (currentModalPage: number) => void;
  fixedRatio: number;
  total: number;
  className?: string;
};

const PDFModal: FC<Props> = ({
  url,
  pageNumber,
  syncPageNumber,
  isOpen,
  toggleOpen,
  fixedRatio,
  total,
  className,
}) => {
  const handleKeyDown = (e: React.KeyboardEvent | any) => {
    if (e.keyCode === keyCodeESC && isOpen) {
      toggleOpen();
    }
  };

  useKeydown(handleKeyDown);
  usePreventScroll(isOpen);

  const [modalPageNumber, setModalPageNumber] = useState(pageNumber);
  useEffect(() => setModalPageNumber(pageNumber), [pageNumber]);

  const goBackModalPage = () => {
    setModalPageNumber(prev => prev - 1);
  };
  const goNextModalPage = () => {
    setModalPageNumber(prev => prev + 1);
  };

  const closeModal = () => {
    toggleOpen();
    syncPageNumber(modalPageNumber);
  };

  const isFirstPage = modalPageNumber <= 1;
  const isLastPage = modalPageNumber >= total;

  if (!isOpen) return null;

  return (
    <Root className={className} onClick={closeModal} id="pdf-modal">
      <ModalContent>
        <CloseModal className="close_icon">
          <SVGIcon src={CloseIcon} />
        </CloseModal>
        <ModalMain onClick={e => e.stopPropagation()}>
          <ArrowButton
            id="pdf-modal__back-button"
            fixedRatio={fixedRatio}
            label={<SVGIcon src={BackIcon} id="pdf-modal__back-button__icon" />}
            onClick={goBackModalPage}
            disabled={isFirstPage}
          />
          <DocumentWrapper fixedRatio={fixedRatio}>
            <Document file={url} options={pdfCMapOptions} loading={<LoadIcon />}>
              <Page
                renderAnnotationLayer={false}
                pageNumber={modalPageNumber}
                loading={<LoadIcon />}
                renderTextLayer={false}
              />
            </Document>
          </DocumentWrapper>

          <ArrowButton
            id="pdf-modal__next-button"
            fixedRatio={fixedRatio}
            label={<SVGIcon src={NextIcon} id="pdf-modal__next-button__icon" />}
            onClick={goNextModalPage}
            disabled={isLastPage}
          />
        </ModalMain>
        <Index id="pdf-modal__index">
          {modalPageNumber} / {total}
        </Index>
      </ModalContent>
    </Root>
  );
};

export default PDFModal;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const Root = styled('div')({
  position: 'fixed',
  zIndex: layer.topUnder1,
  top: 0,
  left: 0,
  height: '100vh',
  width: '100%',
  background: 'rgb(0, 0, 0, 0.6)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  animation: `${fadeIn} 0.3s linear`,
});

const ModalContent = styled('div')({
  textAlign: 'right',
  position: 'relative',
});

const ModalMain = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const ArrowButton = styled(Button)<{ fixedRatio: number }>(props => ({
  zIndex: layer.bottomAbove1,
  width: 80,
  height: '60vh',
  background: 'transparent',
  '&:disabled': {
    background: 'transparent !important',
    opacity: '0.3',
  },
  '&:last-of-type': {
    right: 0,
  },
  [`@media ${bp.md}`]: {
    position: props.fixedRatio > 1 ? 'absolute' : undefined,
    height: props.fixedRatio > 1 ? '80%' : undefined,
    '&:last-of-type': {
      right: 0,
    },
  },
}));

const DocumentWrapper = styled('div')<{ fixedRatio: number }>(props => ({
  height: '85vh',
  width: `calc(85vh * ${props.fixedRatio})`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  '.react-pdf__Document': {
    height: '100%',
  },

  '.react-pdf__Page': {
    height: '100% !important',
  },

  '.react-pdf__Page__canvas': {
    width: 'auto !important',
    height: '100% !important',
    objectFit: 'contain',
  },

  [`@media ${bp.lg}`]: {
    '.react-pdf__Page__canvas': {
      width: '100% !important',
    },
  },

  [`@media ${bp.md}`]: {
    width: props.fixedRatio > 1 ? '100vw' : undefined,
    height: props.fixedRatio > 1 ? `calc(100vw / ${props.fixedRatio})` : undefined,
  },
}));

const CloseModal = styled('span')({
  cursor: 'pointer',
  marginRight: 30,
  svg: {
    width: 16,
  },
});

const Index = styled('div')({
  color: palette.whitePrimary,
  marginTop: 20,
  fontSize: 14,
  textAlign: 'center',
  width: '100%',
});

const LoadIcon = styled(LoadingIndicator)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
});
